
import React, { useState } from 'react';
import CommentEditor from '@/components/community/CommentEditor';
import CommentGuidelines from '@/components/community/CommentGuidelines';
import CommentTopicFilter from '@/components/community/CommentTopicFilter';
import FeaturedDiscussions from '@/components/community/FeaturedDiscussions';
import CommentsList from '@/components/community/CommentsList';
import CommentsHeader from '@/components/community/CommentsHeader';
import { mockComments } from '@/components/community/mockComments';
import { CommentType, SortOption } from '@/components/reader-home/types';
import { 
  filterCommentsByTopic, 
  sortComments, 
  extractAllTopics,
  getFeaturedDiscussions
} from '@/components/community/commentUtils';

interface ArticleCommentsProps {
  articleId: string;
}

const ArticleComments: React.FC<ArticleCommentsProps> = ({ articleId }) => {
  const [comments, setComments] = useState<CommentType[]>(mockComments);
  const [sortOption, setSortOption] = useState<SortOption>("newest");
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  
  const allTopics = extractAllTopics(comments);

  const handleCommentSubmit = (content: string) => {
    const newComment: CommentType = {
      id: Date.now(),
      user: {
        name: "Current User",
        avatar: "/placeholder.svg",
        bio: "Your profile information",
        joinDate: "Member since Jan 2024"
      },
      content,
      likes: 0,
      dislikes: 0,
      timestamp: "Just now",
      topics: [],
      replies: []
    };
    
    setComments([newComment, ...comments]);
  };

  const handleReply = (commentId: number, content: string) => {
    const newReply: CommentType = {
      id: Date.now(),
      user: {
        name: "Current User",
        avatar: "/placeholder.svg",
        bio: "Your profile information",
        joinDate: "Member since Jan 2024"
      },
      content,
      likes: 0,
      dislikes: 0,
      timestamp: "Just now",
      parentId: commentId,
      topics: [],
      replies: []
    };
    
    const addReplyToComment = (comments: CommentType[], parentId: number, newReply: CommentType): CommentType[] => {
      return comments.map(comment => {
        if (comment.id === parentId) {
          return {
            ...comment,
            replies: [...(comment.replies || []), newReply]
          };
        } else if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: addReplyToComment(comment.replies, parentId, newReply)
          };
        }
        return comment;
      });
    };
    
    setComments(addReplyToComment(comments, commentId, newReply));
  };

  const handleSelectTopic = (topic: string) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const clearTopicFilters = () => {
    setSelectedTopics([]);
  };

  const filteredComments = sortComments(
    filterCommentsByTopic(comments, selectedTopics),
    sortOption
  );
  
  const featuredDiscussions = getFeaturedDiscussions(filteredComments);

  return (
    <div>
      <CommentsHeader 
        commentCount={filteredComments.length}
        hasFilters={selectedTopics.length > 0}
        filterCount={selectedTopics.length}
        sortOption={sortOption}
        onSortChange={setSortOption}
        onToggleGuidelines={() => setShowGuidelines(!showGuidelines)}
      />
      
      {showGuidelines && <CommentGuidelines onClose={() => setShowGuidelines(false)} />}
      
      {allTopics.length > 0 && (
        <CommentTopicFilter
          topics={allTopics}
          selectedTopics={selectedTopics}
          onSelectTopic={handleSelectTopic}
          onClearFilters={clearTopicFilters}
        />
      )}
      
      {featuredDiscussions.length > 0 && (
        <FeaturedDiscussions comments={featuredDiscussions} />
      )}
      
      <div className="mb-6">
        <CommentEditor 
          onSubmit={handleCommentSubmit}
          placeholder="Join the discussion..."
          buttonText="Post Comment"
        />
      </div>
      
      <CommentsList 
        comments={filteredComments}
        onReply={handleReply}
        hasFilters={selectedTopics.length > 0}
      />
    </div>
  );
};

export default ArticleComments;
